import React, { useState, useEffect } from 'react';
import {
  MdOutlineDelete,
  MdOutlinePauseCircle,
  MdOutlinePlayCircle,
  MdOutlineHistory,
  MdCheckCircle,
  MdOutlineMoreVert,
  MdClose,
} from 'react-icons/md';
import { getTranslation } from './translations';
import ConfirmModal from './ConfirmModal';
import Toast from './Toast';
import './HistoryPage.css';

export function recordWatchHistoryItem(video) {
  if (!video || !video.title) return;
  try {
    const isPaused = localStorage.getItem('speedotube_pause_history') === 'true';
    if (isPaused) return;

    const saved = localStorage.getItem('speedotube_watch_history');
    const historyList = saved ? JSON.parse(saved) : [];
    
    // Remove existing duplicate entry if present
    const filtered = historyList.filter(
      (item) => item.id !== video.id && item.title !== video.title
    );

    const newItem = {
      id: video.id || `vid_${Date.now()}`,
      title: video.title,
      channelName: video.channelName || video.artist || 'SpeedoTube Creator',
      channelAvatar: video.channelAvatar || '',
      verified: video.verified || false,
      views: video.views || '1 view',
      duration: video.duration || '0:00',
      thumbnailUrl: video.thumbnailUrl || video.thumbnail || (process.env.PUBLIC_URL || '') + '/img/banner.png',
      type: video.type || (video.duration && parseInt(video.duration) < 1 ? 'shorts' : 'videos'),
      watchedAt: new Date().toISOString(),
    };

    const updated = [newItem, ...filtered];
    localStorage.setItem('speedotube_watch_history', JSON.stringify(updated));
    window.dispatchEvent(new Event('speedotube_history_updated'));
  } catch (e) {
    console.warn('Failed to record watch history item:', e);
  }
}

export default function HistoryPage({ userProfile, onNavigate }) {
  const [activeFilter, setActiveFilter] = useState('All'); // 'All' | 'Videos' | 'Shorts' | 'Music'
  const [searchQuery, setSearchQuery] = useState('');
  const [historyItems, setHistoryItems] = useState([]);
  const [isPaused, setIsPaused] = useState(() => {
    return localStorage.getItem('speedotube_pause_history') === 'true';
  });
  const [isClearModalOpen, setIsClearModalOpen] = useState(false);
  const [isPauseModalOpen, setIsPauseModalOpen] = useState(false);
  const [toastState, setToastState] = useState({
    isOpen: false,
    message: '',
    icon: null,
  });

  const triggerToast = (message, icon) => {
    setToastState({ isOpen: false, message: '', icon: null });
    setTimeout(() => {
      setToastState({ isOpen: true, message, icon });
    }, 50);
  };

  // Real fetch from localStorage without creating dummy records
  const loadHistoryData = () => {
    try {
      const saved = localStorage.getItem('speedotube_watch_history');
      const items = saved ? JSON.parse(saved) : [];
      setHistoryItems(items);
    } catch (e) {
      console.warn('Failed to parse watch history:', e);
      setHistoryItems([]);
    }
  };

  useEffect(() => {
    loadHistoryData();
    const handleSearchEvent = (e) => {
      setSearchQuery(e.detail || '');
    };
    window.addEventListener('speedotube_history_updated', loadHistoryData);
    window.addEventListener('speedotube_search_history', handleSearchEvent);
    return () => {
      window.removeEventListener('speedotube_history_updated', loadHistoryData);
      window.removeEventListener('speedotube_search_history', handleSearchEvent);
    };
  }, []);

  const handleConfirmClear = () => {
    localStorage.removeItem('speedotube_watch_history');
    setHistoryItems([]);
    window.dispatchEvent(new Event('speedotube_history_updated'));
    triggerToast('Watch history cleared', MdOutlineDelete);
  };

  const handleConfirmPause = () => {
    const nextState = !isPaused;
    setIsPaused(nextState);
    localStorage.setItem('speedotube_pause_history', nextState ? 'true' : 'false');
    if (nextState) {
      triggerToast('Watch history paused', MdOutlinePauseCircle);
    } else {
      triggerToast('Watch history turned on', MdOutlinePlayCircle);
    }
  };

  const handleRemoveSingleItem = (e, item) => {
    if (e) {
      if (e.stopPropagation) e.stopPropagation();
      if (e.preventDefault) e.preventDefault();
    }
    const targetId = typeof item === 'object' ? item.id : item;
    const removedItem = typeof item === 'object' ? item : historyItems.find((i) => i.id === targetId);
    const videoTitle = removedItem?.title || 'Video';

    const updated = historyItems.filter((i) => i.id !== targetId);
    setHistoryItems(updated);
    localStorage.setItem('speedotube_watch_history', JSON.stringify(updated));
    window.dispatchEvent(new Event('speedotube_history_updated'));

    triggerToast(`"${videoTitle}" has been removed from watch history`);
  };

  // Filter items based on active filter pill and search query
  const filteredItems = historyItems.filter((item) => {
    const matchesQuery =
      !searchQuery ||
      item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.channelName.toLowerCase().includes(searchQuery.toLowerCase());

    if (!matchesQuery) return false;

    if (activeFilter === 'Videos') {
      return item.type === 'videos' || !item.type;
    }
    if (activeFilter === 'Shorts') {
      return item.type === 'shorts';
    }
    if (activeFilter === 'Music') {
      return item.type === 'music';
    }

    return true;
  });

  // Group real filtered items by Date (Today, Yesterday, Older)
  const groupItemsByDate = (items) => {
    const now = new Date();
    const todayStr = now.toDateString();
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayStr = yesterday.toDateString();

    const groups = { Today: [], Yesterday: [], Older: [] };

    items.forEach((item) => {
      const itemDate = new Date(item.watchedAt || Date.now()).toDateString();
      if (itemDate === todayStr) {
        groups.Today.push(item);
      } else if (itemDate === yesterdayStr) {
        groups.Yesterday.push(item);
      } else {
        groups.Older.push(item);
      }
    });

    return groups;
  };

  const grouped = groupItemsByDate(filteredItems);

  return (
    <div className="yt-history-page">
      <div className="yt-history-container">
        {/* Left Column: Watch History Content Area */}
        <div className="yt-history-main-content">
          {/* Header Title & Status Banner */}
          <div className="yt-history-header-row">
            <h1 className="yt-history-title">Watch history</h1>
            {isPaused && (
              <div className="yt-history-paused-banner">
                <MdOutlinePauseCircle size={18} />
                <span>Watch history is paused</span>
              </div>
            )}
          </div>

          {/* Filter Pills Row (All, Videos, Shorts, Music) */}
          <div className="yt-history-filter-pills">
            {['All', 'Videos', 'Shorts', 'Music'].map((filter) => (
              <button
                key={filter}
                className={`yt-history-pill ${activeFilter === filter ? 'active' : ''}`}
                onClick={() => setActiveFilter(filter)}
              >
                {filter}
              </button>
            ))}
          </div>

          {/* History List or Authentic Empty State */}
          {filteredItems.length === 0 ? (
            <div className="yt-history-empty-state">
              <div className="yt-history-empty-icon">
                <MdOutlineHistory size={72} />
              </div>
              <h3 className="yt-history-empty-title">
                {searchQuery ? 'No matching videos found' : 'Keep track of what you watch'}
              </h3>
              <p className="yt-history-empty-subtitle">
                {searchQuery
                  ? `No search results in watch history for "${searchQuery}".`
                  : "Watch history isn't showing any videos yet. Videos you watch or upload from local in the future will appear here."}
              </p>
            </div>
          ) : (
            <div className="yt-history-sections">
              {['Today', 'Yesterday', 'Older'].map((groupKey) => {
                const groupItems = grouped[groupKey];
                if (!groupItems || groupItems.length === 0) return null;

                return (
                  <div key={groupKey} className="yt-history-group">
                    <h2 className="yt-history-date-header">{groupKey}</h2>
                    <div className="yt-history-items-list">
                      {groupItems.map((item) => (
                        <div
                          key={item.id}
                          className="yt-history-item-card"
                          onClick={(e) => {
                            if (e.target.closest('.yt-history-item-remove-btn')) return;
                            const targetWatchPath = `/watch?v=${item.videoUrlId || item.id}`;
                            if (onNavigate) {
                              onNavigate(targetWatchPath);
                            } else {
                              window.history.pushState({}, '', targetWatchPath);
                              window.dispatchEvent(
                                new CustomEvent('speedotube_navigate', {
                                  detail: { path: targetWatchPath, videoId: item.videoUrlId || item.id },
                                })
                              );
                            }
                          }}
                          style={{ cursor: 'pointer' }}
                        >
                          <div className="yt-history-thumb-container">
                            <img src={item.thumbnailUrl} alt={item.title} className="yt-history-thumb-img" />
                            <span className="yt-history-duration">{item.duration}</span>
                          </div>

                          <div className="yt-history-item-info">
                            <h3 className="yt-history-item-title" title={item.title}>
                              {item.title}
                            </h3>
                            <div className="yt-history-item-channel">
                              <span>{item.channelName}</span>
                              {item.verified && <MdCheckCircle className="yt-verified-icon" size={14} title="Verified" />}
                              <span className="yt-history-dot">•</span>
                              <span>{item.views} views</span>
                            </div>
                            <p className="yt-history-item-desc">
                              Watched on SpeedoTube • High definition playback
                            </p>
                          </div>

                          <button
                            type="button"
                            className="yt-history-item-remove-btn"
                            title="Remove from watch history"
                            aria-label="Remove from watch history"
                            onClick={(e) => handleRemoveSingleItem(e, item)}
                          >
                            <MdClose size={20} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Right Column: History Control Options Sidebar */}
        <aside className="yt-history-sidebar">
          <div className="yt-history-control-group">
            <button className="yt-history-control-btn" onClick={() => setIsClearModalOpen(true)}>
              <MdOutlineDelete size={22} />
              <span>Clear all watch history</span>
            </button>

            <button className="yt-history-control-btn" onClick={() => setIsPauseModalOpen(true)}>
              {isPaused ? <MdOutlinePlayCircle size={22} /> : <MdOutlinePauseCircle size={22} />}
              <span>{isPaused ? 'Turn on watch history' : 'Pause watch history'}</span>
            </button>
          </div>
        </aside>
      </div>

      {/* Smooth Pop-up Confirm Modal for Clear Watch History */}
      <ConfirmModal
        isOpen={isClearModalOpen}
        title="Clear watch history?"
        message="Clear watch history from this device? This will clear all watch history recorded on SpeedoTube in this browser."
        confirmText="Clear watch history"
        cancelText="Cancel"
        icon={MdOutlineDelete}
        danger={true}
        onConfirm={handleConfirmClear}
        onClose={() => setIsClearModalOpen(false)}
      />

      {/* Smooth Pop-up Confirm Modal for Pause / Turn On Watch History */}
      <ConfirmModal
        isOpen={isPauseModalOpen}
        title={isPaused ? 'Turn on watch history?' : 'Pause watch history?'}
        message={
          isPaused
            ? 'SpeedoTube watch history will resume recording videos you watch on this browser.'
            : "Pausing SpeedoTube watch history can make it harder to find videos you've watched, and you may see fewer recommendations for new videos."
        }
        confirmText={isPaused ? 'Turn on' : 'Pause'}
        cancelText="Cancel"
        icon={isPaused ? MdOutlinePlayCircle : MdOutlinePauseCircle}
        danger={false}
        onConfirm={handleConfirmPause}
        onClose={() => setIsPauseModalOpen(false)}
      />

      {/* Screen Animation Toast Notification */}
      <Toast
        isOpen={toastState.isOpen}
        message={toastState.message}
        icon={toastState.icon}
        onClose={() => setToastState((prev) => ({ ...prev, isOpen: false }))}
      />
    </div>
  );
}
